import React, { useCallback, useEffect, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import {
    Valuation,
    valuationService,
    ValuationMethodPayload,
    ValuationPayload,
} from '../saved-company/valuation.service';
import './valuation.scss';

interface Params {
    id: string;
}

const POLL_INTERVAL_MS = 4000;
const POLL_MAX_ATTEMPTS = 120;

const formatEur = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '—';
    return `€${Math.round(value).toLocaleString('en-US')}`;
};

const formatPct = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '—';
    return `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;
};

const formatMultiple = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '—';
    return `${value.toFixed(2)}x`;
};

const SOURCE_LABELS: Record<string, string> = {
    finnai: 'FinnAI (PDF extraction)',
    ebeszamolo: 'e-beszámoló (Hungarian registry)',
    companies_house: 'Companies House (UK registry)',
    pappers: 'Pappers (French registry)',
    sec_edgar: 'SEC EDGAR',
    yahoo_finance: 'Yahoo Finance',
    israel: 'Israel Corporations Registry',
    us_private_federated: 'US Private Federated',
    document_extraction_pass1: 'Document re-extraction (LLM, pass 1)',
    document_extraction_pass2: 'Document re-extraction (LLM, pass 2)',
};

const formatSource = (source?: string | null): string => {
    if (!source) return '';
    return SOURCE_LABELS[source] || source;
};

const FinancialRow: React.FC<{ label: string; value?: number | null; source?: string | null }> = ({ label, value, source }) => (
    <>
        <dt>{label}</dt>
        <dd>
            <span className="valuation-financial-value">{formatEur(value)}</span>
            {value != null && source && (
                <span className="valuation-financial-source" title={source}>{formatSource(source)}</span>
            )}
        </dd>
    </>
);

const MethodCard: React.FC<{ title: string; subtitle: string; method?: ValuationMethodPayload; accent: 'primary' | 'success' | 'muted'; isEquity?: boolean }> = ({
    title,
    subtitle,
    method,
    accent,
    isEquity,
}) => {
    if (!method || !method.applicable) {
        return (
            <div className={`valuation-card valuation-card--muted`}>
                <div className="valuation-card__header">
                    <span className={`valuation-card__tag valuation-card__tag--${accent}`}>{title}</span>
                    <span className="valuation-card__subtitle">{subtitle}</span>
                </div>
                <p className="valuation-card__not-applicable">{method?.reason || 'Not applicable for this company.'}</p>
            </div>
        );
    }

    const finalLabel = isEquity ? 'Equity Value' : 'Enterprise Value';
    const finalValue = isEquity ? (method.equity ?? method.finalEnterpriseValue) : method.finalEnterpriseValue;

    return (
        <div className={`valuation-card valuation-card--${accent}`}>
            <div className="valuation-card__header">
                <span className={`valuation-card__tag valuation-card__tag--${accent}`}>{title}</span>
                <span className="valuation-card__subtitle">{subtitle}</span>
            </div>
            <dl className="valuation-card__grid">
                <dt>Multiple</dt><dd>{formatMultiple(method.multiple)}</dd>
                <dt>Base value</dt><dd>{formatEur(method.baseValue)}</dd>
                <dt>Adjustments</dt><dd>{formatPct(method.adjustmentPct)}</dd>
                <dt>Adjusted</dt><dd>{formatEur(method.adjustedValue)}</dd>
                <dt>Size discount</dt><dd>{formatPct(method.sizeDiscountPct)}</dd>
                {!isEquity && (<>
                    <dt>Enterprise value</dt><dd className="valuation-card__final">{formatEur(method.finalEnterpriseValue)}</dd>
                    <dt>Net debt → equity</dt><dd>{formatEur(method.equity)}</dd>
                </>)}
                {isEquity && (<>
                    <dt className="valuation-card__final-label">{finalLabel}</dt>
                    <dd className="valuation-card__final">{formatEur(finalValue)}</dd>
                </>)}
            </dl>
        </div>
    );
};

interface ProgressLogEntry {
    timestamp: number;
    step: string;
    message: string;
    pct?: number | null;
}

const ValuationPageComponent: React.FC<RouteComponentProps<Params>> = ({ match, history }) => {
    const valuationId = parseInt(match.params.id, 10);
    const [valuation, setValuation] = useState<Valuation | null>(null);
    const [payload, setPayload] = useState<ValuationPayload | null>(null);
    const [progress, setProgress] = useState<number>(0);
    const [error, setError] = useState<string | null>(null);
    const [loaded, setLoaded] = useState(false);
    const [progressLog, setProgressLog] = useState<ProgressLogEntry[]>([]);

    const fetchOnce = useCallback(async (): Promise<Valuation | null> => {
        const response = await valuationService.getValuation(valuationId);
        if (response?.status === 404) {
            setError('Valuation not found.');
            return null;
        }
        if (response?.status !== 200) {
            setError(`Could not load valuation (HTTP ${response?.status ?? 'no response'}).`);
            return null;
        }
        return response.data as Valuation;
    }, [valuationId]);

    useEffect(() => {
        if (!valuationId || Number.isNaN(valuationId)) {
            setError('Invalid valuation id.');
            setLoaded(true);
            return;
        }

        let cancelled = false;

        const run = async () => {
            for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
                if (cancelled) return;

                const v = await fetchOnce();
                if (!v) {
                    setLoaded(true);
                    return;
                }

                setValuation(v);
                setLoaded(true);

                if (v.progressStep || v.progressMessage) {
                    const step = v.progressStep || '';
                    const message = v.progressMessage || '';
                    setProgressLog((prev) => {
                        const last = prev[prev.length - 1];
                        if (last && last.step === step && last.message === message) {
                            return prev;
                        }
                        return [...prev, { timestamp: Date.now(), step, message, pct: v.progressPct }];
                    });
                }

                if (v.status === 'Completed' || v.status === 'Failed' || v.status === 'Refunded') {
                    setPayload(valuationService.parseValuationPayload(v));
                    return;
                }

                setProgress(v.progressPct ?? Math.min(95, 10 + attempt * 4));
                await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
            }

            if (!cancelled) {
                setError('Valuation polling timed out.');
            }
        };

        run();

        return () => {
            cancelled = true;
        };
    }, [valuationId, fetchOnce]);

    if (!loaded) {
        return (
            <div className="section section--content">
                <div className="container">
                    <div className="valuation-page">
                        <h1 className="title title-l">Loading valuation…</h1>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="section section--content">
                <div className="container">
                    <div className="valuation-page">
                        <h1 className="title title-l">Valuation</h1>
                        <p className="valuation-page__error">{error}</p>
                        <button className="button button--secondary" onClick={() => history.goBack()}>
                            <span className="button__inner"><span>Back</span></span>
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!valuation) {
        return null;
    }

    const isRunning = valuation.status === 'Pending' || valuation.status === 'Running';

    return (
        <div className="section section--content">
            <div className="container">
                <div className="valuation-page">
                    <div className="valuation-page__header">
                        <h1 className="title title-l">Valuation</h1>
                        {payload?.companyName && (
                            <p className="title title-s valuation-page__subtitle">
                                {payload.companyName}
                                {payload.registrationCountry && <> · {payload.registrationCountry}</>}
                                {payload.industry && <> · {payload.industry}</>}
                            </p>
                        )}
                    </div>

                    {isRunning && (
                        <div className="valuation-progress">
                            <div className="valuation-progress__bar">
                                <div className="valuation-progress__fill" style={{ width: `${progress}%` }} />
                            </div>
                            <p>
                                Running valuation… this can take a few minutes for live data.
                                {progressLog.length > 0 && (
                                    <> Last step: <strong>{progressLog[progressLog.length - 1].step}</strong>.</>
                                )}
                            </p>
                            {progressLog.length > 0 && (
                                <div className="valuation-log">
                                    <div className="valuation-log__header">Live progress</div>
                                    <ol className="valuation-log__list">
                                        {progressLog.map((entry, i) => {
                                            const time = new Date(entry.timestamp);
                                            const stamp = `${time.getHours().toString().padStart(2, '0')}:${time
                                                .getMinutes()
                                                .toString()
                                                .padStart(2, '0')}:${time.getSeconds().toString().padStart(2, '0')}`;
                                            const isLast = i === progressLog.length - 1;
                                            return (
                                                <li
                                                    key={`${entry.step}-${entry.timestamp}-${i}`}
                                                    className={`valuation-log__entry${isLast ? ' valuation-log__entry--current' : ''}`}
                                                >
                                                    <span className="valuation-log__time">{stamp}</span>
                                                    <span className="valuation-log__step">{entry.step}</span>
                                                    <span className="valuation-log__msg">{entry.message}</span>
                                                </li>
                                            );
                                        })}
                                    </ol>
                                </div>
                            )}
                        </div>
                    )}

                    {(valuation.status === 'Failed' || valuation.status === 'Refunded') && (
                        <div className="valuation-page__error">
                            <p className="bold">Valuation could not be completed.</p>
                            <p>{valuation.errorMessage || 'Unknown error.'}</p>
                            <p>Your {valuation.creditsSpent.toLocaleString('en-US')} credits were refunded.</p>
                        </div>
                    )}

                    {valuation.status === 'Completed' && payload && (
                        <div className="valuation-results">
                            {payload.aiSummary && (
                                <div className="valuation-summary">
                                    <h2 className="title title-s">AI Summary</h2>
                                    <p>{payload.aiSummary}</p>
                                </div>
                            )}

                            <div className="valuation-grid">
                                <MethodCard
                                    title="EV/EBITDA"
                                    subtitle="Enterprise Value Method"
                                    method={payload.evEbitda}
                                    accent="primary"
                                />
                                <MethodCard
                                    title="P/E"
                                    subtitle="Price / Earnings Method"
                                    method={payload.pe}
                                    accent="success"
                                    isEquity
                                />
                                <div className="valuation-card valuation-card--muted">
                                    <div className="valuation-card__header">
                                        <span className="valuation-card__tag valuation-card__tag--muted">NAV</span>
                                        <span className="valuation-card__subtitle">Net Asset Value</span>
                                    </div>
                                    <dl className="valuation-card__grid">
                                        <dt className="valuation-card__final-label">Net Asset Value</dt>
                                        <dd className="valuation-card__final">{formatEur(payload.netAssetValue)}</dd>
                                    </dl>
                                </div>
                            </div>

                            {payload.financials && (
                                <div className="valuation-financials">
                                    <h2 className="title title-s">Financial Inputs</h2>
                                    <dl className="valuation-card__grid">
                                        <FinancialRow label="Revenue" value={payload.financials.revenue} source={payload.financials.revenueSource} />
                                        <FinancialRow label="EBITDA" value={payload.financials.ebitda} source={payload.financials.ebitdaSource} />
                                        <FinancialRow label="Net income" value={payload.financials.netIncome} source={payload.financials.netIncomeSource} />
                                        <FinancialRow label="Total assets" value={payload.financials.totalAssets} source={payload.financials.totalAssetsSource} />
                                        <FinancialRow label="Total liabilities" value={payload.financials.totalLiabilities} source={payload.financials.totalLiabilitiesSource} />
                                        <FinancialRow label="Cash" value={payload.financials.cash} source={payload.financials.cashSource} />
                                        <FinancialRow label="Total debt" value={payload.financials.totalDebt} source={payload.financials.totalDebtSource} />
                                    </dl>
                                </div>
                            )}

                            {payload.warnings && payload.warnings.length > 0 && (
                                <div className="valuation-warnings">
                                    <h3 className="title title-xs">Notes</h3>
                                    <ul>
                                        {payload.warnings.map((w, i) => (<li key={i}>{w}</li>))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="valuation-page__actions">
                        <button className="button button--secondary" onClick={() => history.goBack()}>
                            <span className="button__inner"><span>Back to company</span></span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const ValuationPage = withRouter(ValuationPageComponent);
